import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Users, BookOpen, Shield, Play, ShoppingBag, Heart, ExternalLink, Music, Radio } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { SemearFontStyle, YHUHName, YHUSHAName } from "../components/SemearNames";
import ScriptureOfDay from "../components/ScriptureOfDay";


const FLAG_URL = "https://customer-assets.emergentagent.com/job_cf0b5491-f8c8-4451-aded-31dbb7c32d76/artifacts/rb7dp60t_Jonesaica%20Yahshar%27al%20flag%202.png";
const COAT_OF_ARMS = "https://customer-assets.emergentagent.com/job_92e13f48-99c4-452a-b014-1be6a3b8b28b/artifacts/e3352bxk_Mock%20up%20Jonesaica%20Yah%E2%80%99shar%E2%80%99al.png";
const SHABA_INTRO_VIDEO = "https://customer-assets.emergentagent.com/job_8ed5ec52-990e-4af6-908a-8b1627b4f2b5/artifacts/7owgx5f1_shaba%20shara%20intro.mp4";
const QADASH_SHEEP = "https://customer-assets.emergentagent.com/job_cf0b5491-f8c8-4451-aded-31dbb7c32d76/artifacts/leiwo3fc_IMG_4439.jpg";

const homepageArtists = [
  {
    key: "shaba",
    eyebrow: "YY Records & Publishing · Artist",
    name: "Shaba Sharah",
    headline: "A Qadash Sound Calling the Scattered Ones Back",
    description: "Reggae, dancehall, testimony, Abaryt study, appointed-time sound, scripture study, and creative work rooted in the Ancient Path.",
    href: "/brands/shaba",
    cta: "Visit Shaba Sharah",
    media: "video",
  },
  {
    key: "yah-girls",
    eyebrow: "YY Records & Publishing · Artist Group",
    name: "YaHUaH's Girls",
    headline: "Songs, movement, Turah and learning made alive",
    description: "A lively learning and music space teaching Turah, YaHUaH's laws, statutes and commands, Abaryt, ABC, numbers, parts of speech and more through songs, movement, repetition, stories and activities.",
    href: "/YaHsgirls",
    cta: "Visit YaHUaH's Girls",
    media: "placeholder",
  },
];

const pillars = [
  {
    icon: BookOpen,
    title: "Scripture-Based Teaching",
    description: "Raising up minors in truth, discipline, and ecclesiastic purpose according to the Qadash Scriptures.",
  },
  {
    icon: Shield,
    title: "Lawful Nationality",
    description: "Breaking free from Babylon's system through sound money, sovereignty, and righteous governance.",
  },
  {
  icon: Users,
  title: "Nation Building",
  description: (
    <>
      Building a qadash community of the called-out ones, united in the purpose of <YHUHName />.
    </>
  ),
},
];

const spotlights = [
  {
    title: "Fight Fi Tash — Advocacy",
    description: "State-sanctioned child abuse cover-up exposed. Every judge, clerk, and oath-breaker documented.",
    href: "/brands/fightfitash",
    color: "bg-scarlett",
    icon: Shield,
  },
  {
    title: "Shaba Sharah — Music",
    description: "Scripture-based reggae, dancehall, and prophetic fire. $0.99 singles, 20s previews, and monthly support tiers.",
    href: "/brands/shaba",
    color: "bg-royal-purple",
    icon: Music,
  },
  {
    title: "Media Hub",
    description: "Original music, cinematic scripture movies, feast day documentaries, and prophetic teachings.",
    href: "/media",
    color: "bg-royal-blue",
    icon: Play,
  },
  {
    title: "Merch & Shop",
    description: "Support the nation with official merchandise, digital downloads, and kingdom resources.",
    href: "/shop",
    color: "bg-kingdom-gold",
    icon: ShoppingBag,
  },
  {
    title: "Infrastructure / Solar",
    description: "Sustainable solutions for self-reliance and independence from Babylon's systems.",
    href: "https://solutions.yahsharal.info/",
    external: true,
    color: "bg-slate-700",
    icon: ExternalLink,
  },
];

export default function Home() {
  const [artistIndex, setArtistIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setArtistIndex((current) => (current + 1) % homepageArtists.length);
    }, 8000);
    return () => clearInterval(timer);
  }, []);

  const activeArtist = homepageArtists[artistIndex];

  return (
    <div className="min-h-screen" data-testid="home-page">
      <SemearFontStyle />
  
      {/* Daily Scripture */}
      <section className="bg-parchment py-8 md:py-10" aria-label="Scripture of the day">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScriptureOfDay />
        </div>
      </section>

      {/* Yashar'al Live — promoted product */}
      <section
        className="bg-royal-blue text-white border-y-4 border-kingdom-gold"
        aria-label="Yasharal Live"
        data-testid="home-yasharal-live-ad"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12 md:py-16">
          <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-8 lg:gap-14 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-kingdom-gold px-3 py-1.5 text-royal-blue font-black uppercase tracking-[0.16em] text-[11px] sm:text-xs shadow-lg">
                <span className="inline-block h-2 w-2 rounded-full bg-scarlett animate-pulse" aria-hidden="true" />
                New · Yashar’al Live
              </div>

              <h2 className="font-heading mt-5 text-3xl sm:text-5xl lg:text-6xl leading-[1.02]">
                Your meeting. Your studio.
                <span className="block text-kingdom-gold">Your broadcast.</span>
              </h2>

              <p className="mt-4 max-w-2xl text-base sm:text-lg leading-relaxed text-white/90">
                Meet face-to-face, record sessions, produce from the browser Studio, and broadcast outward from one Yashar’al platform.
              </p>

              <div className="mt-5 grid grid-cols-2 gap-2 text-[11px] sm:flex sm:flex-wrap sm:text-xs font-bold uppercase tracking-wider">
                {["Interactive meetings", "Recording", "Browser Studio", "Live broadcast"].map((item) => (
                  <span key={item} className="border border-white/20 bg-white/10 px-2.5 py-2 text-center">
                    {item}
                  </span>
                ))}
              </div>

              <div className="mt-6 flex flex-col sm:flex-row sm:flex-wrap gap-2.5">
                <Link
                  to="/yasharal-live/signup"
                  className="inline-flex min-h-11 items-center justify-center gap-2 bg-kingdom-gold text-royal-blue font-black px-5 py-3 shadow-lg hover:brightness-105"
                >
                  Start with Yashar’al Live <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/yasharal-live/login#features"
                  className="inline-flex min-h-11 items-center justify-center gap-2 bg-white text-royal-blue font-bold px-5 py-3 hover:bg-white/90"
                >
                  Explore plans & features
                </Link>
                <Link
                  to="/yasharal-live/login"
                  className="inline-flex min-h-11 items-center justify-center px-5 py-3 border border-white/60 font-semibold hover:bg-white/10"
                >
                  Sign in
                </Link>
              </div>

              <div className="mt-5 inline-flex items-center gap-2 text-xs sm:text-sm font-semibold text-kingdom-gold">
                <span className="rounded-full border border-kingdom-gold/50 px-3 py-1">App coming soon</span>
                <span className="text-white/65">Mobile + desktop access planned</span>
              </div>
            </div>

            <div className="hidden sm:block">
              <div className="border border-white/20 bg-slate-950/90 p-3 shadow-2xl">
                <div className="flex items-center justify-between border-b border-white/10 px-2 pb-3">
                  <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full bg-scarlett animate-pulse" />
                    <span className="text-xs font-black uppercase tracking-[0.2em] text-white">Live Studio</span>
                  </div>
                  <span className="text-[11px] font-semibold text-kingdom-gold">YASHAR’AL LIVE</span>
                </div>

                <div className="mt-3 grid min-h-[230px] grid-cols-[1fr_104px] gap-3">
                  <div className="relative flex items-center justify-center overflow-hidden bg-gradient-to-br from-slate-800 to-slate-950 p-5">
                    <div className="text-center">
                      <Radio className="mx-auto h-10 w-10 text-kingdom-gold" aria-hidden="true" />
                      <div className="mt-3 font-heading text-xl">Broadcast Stage</div>
                      <div className="mt-2 text-xs text-white/60">Meeting → Studio → Audience</div>
                    </div>
                    <div className="absolute bottom-3 left-3 flex gap-2">
                      <span className="rounded bg-scarlett px-2 py-1 text-[10px] font-black">LIVE</span>
                      <span className="rounded bg-black/60 px-2 py-1 text-[10px]">REC</span>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {["Host", "Guest", "Screen"].map((label) => (
                      <div key={label} className="flex h-[68px] items-end border border-white/10 bg-white/5 p-2">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-white/70">{label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-3 grid grid-cols-4 gap-2">
                  {[
                    ["MEET", Users],
                    ["RECORD", Radio],
                    ["PRODUCE", Play],
                    ["BROADCAST", Radio],
                  ].map(([label, Icon]) => (
                    <div key={label} className="flex items-center justify-center gap-1.5 border border-white/10 bg-white/5 px-2 py-2 text-[10px] font-bold tracking-wider text-kingdom-gold">
                      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                      {label}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Hero Section */}
      <section className="relative bg-parchment overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 md:py-20">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            <div className="animate-slide-up">
              <span className="inline-block text-kingdom-gold text-sm font-semibold uppercase tracking-wider mb-4">
                BRING TASH HOME
              </span>
              <h1 className="heading-hero text-royal-blue mb-4 sm:mb-6">
                A Nation Qadash
              </h1>
              <p className="body-large text-slate-700 mb-6 sm:mb-8 max-w-xl">
                The Kingdom of Yunah'Shach'Qal Yashar'al is not established to replace the Kingdom of <YHUHName /> (YaHUaH), but to serve as a gathering place for those seeking to walk according to His Instructions while preparing themselves, their households, and future generations for His coming Kingdom. Seeking the Ancient Path, restoring the Turah of <YHUHName />, preserving the ancient ways, and strengthening households and communities through wisdom, craftsmanship, stewardship, and mutual support.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/become-national">
                  <Button className="btn-gold px-8 py-3 text-base w-full sm:w-auto" data-testid="hero-join-btn">
                    Join the Nation
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/governance/declaration">
                  <Button variant="outline" className="btn-outline-royal px-8 py-3 text-base w-full sm:w-auto" data-testid="hero-declaration-btn">
                    Read Declaration
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative animate-slide-up stagger-2">
              <div className="relative z-10">
                <img
                  src={FLAG_URL}
                  alt="Flag of Yunah'Shach'Qal Yashar'al"
                  className="w-full max-w-md mx-auto rounded-sm shadow-2xl"
                />
              </div>
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-kingdom-gold/20 rounded-full blur-2xl" />
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-royal-purple/20 rounded-full blur-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Mission Block */}
      <section className="bg-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="heading-section text-royal-blue mb-8">Our Mission</h2>
          <div className="bg-parchment p-8 rounded-sm border-l-4 border-kingdom-gold">
            <p className="body-large text-slate-700 leading-relaxed">
              To return to the law of Creation. To proclaim the Name <YHUHName />/<YHUSHAName /> (YaHUSHA). To build a nation of the qadashym (called-out ones). Whether you're searching for your true identity, seeking spiritual clarity, or trying to escape the traps of this world—you've found the frontline of truth.
            </p>
          </div>
        </div>
      </section>

    {/* YY Records & Publishing — Artists */}
      <section
        className="bg-royal-blue py-16"
        data-testid="homepage-artists-section"
        style={{ backgroundColor: "#1D4ED8" }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <span className="text-sm font-semibold uppercase tracking-wider text-kingdom-gold">YY Records &amp; Publishing</span>
              <h2 className="heading-section mt-2 text-[#f5ead2]">Artists</h2>
            </div>
            <div className="flex gap-2" aria-label="Choose artist">
              {homepageArtists.map((artist, index) => (
                <button
                  key={artist.key}
                  type="button"
                  onClick={() => setArtistIndex(index)}
                  className={`h-2.5 rounded-full transition-all ${index === artistIndex ? "w-10 bg-kingdom-gold" : "w-2.5 bg-white/40 hover:bg-white/70"}`}
                  aria-label={`Show ${artist.name}`}
                  aria-pressed={index === artistIndex}
                />
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div key={activeArtist.key} className="animate-slide-up">
              {activeArtist.media === "video" ? (
                <video
                  className="rounded-sm shadow-xl w-full max-w-lg mx-auto"
                  controls
                  playsInline
                  preload="metadata"
                  data-testid="shaba-home-video"
                >
                  <source src={SHABA_INTRO_VIDEO} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              ) : (
                <div className="mx-auto flex aspect-video w-full max-w-lg items-center justify-center overflow-hidden rounded-sm border-2 border-kingdom-gold/50 bg-gradient-to-br from-[#f7d66d] via-[#f3b7cf] to-[#9fd8e8] shadow-xl" data-testid="yah-girls-home-placeholder">
                  <div className="text-center text-royal-blue">
                    <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-full border-4 border-white/70 bg-white/45 font-heading text-4xl font-black shadow-lg">YG</div>
                    <p className="mt-4 text-xs font-black uppercase tracking-[0.2em]">YaHUaH&apos;s Girls</p>
                    <p className="mt-1 text-xs font-semibold">Temporary artist art</p>
                  </div>
                </div>
              )}
            </div>

            <div key={`${activeArtist.key}-copy`} className="animate-slide-up">
              <span className="text-sm font-semibold uppercase tracking-wider text-kingdom-gold">
                {activeArtist.eyebrow}
              </span>
              <h3 className="heading-section mt-2 mb-4 text-[#f5ead2]">
                {activeArtist.headline}
              </h3>
              <p className="mb-6 text-base leading-relaxed text-[#f5ead2]">
                {activeArtist.description}
              </p>

              {activeArtist.key === "yah-girls" && (
                <div className="mb-6 flex flex-wrap gap-2 text-xs font-bold uppercase tracking-wider text-royal-blue">
                  {["Turah", "Abaryt", "Songs", "ABC + Numbers", "Activities"].map((item) => (
                    <span key={item} className="bg-kingdom-gold px-3 py-2">{item}</span>
                  ))}
                </div>
              )}

              <div className="flex flex-wrap gap-4">
                <Link
                  to={activeArtist.href}
                  className="inline-flex items-center gap-2 font-semibold text-kingdom-gold hover:underline"
                  data-testid={`home-artist-${activeArtist.key}-link`}
                >
                  {activeArtist.cta}
                  <ArrowRight className="w-4 h-4" />
                </Link>

                {activeArtist.key === "shaba" ? (
                  <>
                    <a href="https://www.youtube.com/@shabasharah" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 font-semibold text-kingdom-gold hover:underline">
                      YouTube <ExternalLink className="w-4 h-4" />
                    </a>
                    <a href="https://www.tiktok.com/@shabasharah" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 font-semibold text-kingdom-gold hover:underline">
                      TikTok <ExternalLink className="w-4 h-4" />
                    </a>
                  </>
                ) : (
                  <>
                    <Link to="/ancient-path-youth" className="inline-flex items-center gap-2 font-semibold text-kingdom-gold hover:underline">
                      Ancient Path Youth <ArrowRight className="w-4 h-4" />
                    </Link>
                    <Link to="/free-resources" className="inline-flex items-center gap-2 font-semibold text-kingdom-gold hover:underline">
                      Free Resources <ArrowRight className="w-4 h-4" />
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Free Resources */}
      <section className="bg-[#fff8ed] py-16" data-testid="home-free-resources-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
            <div>
              <span className="text-sm font-black uppercase tracking-[0.18em] text-kingdom-gold">Free learning library</span>
              <h2 className="heading-section mt-2 text-royal-blue">Resources for every age group</h2>
              <p className="mt-4 max-w-2xl text-slate-600">
                Browse free lessons, reference material, printables, games and activities for early learners, youth, teens, adults and whole households. Filter by age, subject and resource type.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/free-resources" className="inline-flex min-h-11 items-center justify-center bg-royal-blue px-5 py-3 font-black text-white">
                  Browse Free Resources <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
                <Link to="/ancient-path-youth" className="inline-flex min-h-11 items-center justify-center border border-royal-blue px-5 py-3 font-bold text-royal-blue hover:bg-royal-blue/5">
                  Ancient Path Youth
                </Link>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <Link to="/free-resources/alap-bayt" className="bg-white p-6 shadow-sm ring-1 ring-black/5 transition-transform hover:-translate-y-1">
                <div className="text-5xl font-black text-royal-blue">𐤀</div>
                <h3 className="mt-4 font-heading text-xl font-black text-royal-blue">Alap Bayt</h3>
                <p className="mt-2 text-sm text-slate-600">Ancient Abaryt letters, sounds, pictographs, meanings and numbers.</p>
                <span className="mt-4 inline-flex items-center text-sm font-bold text-royal-blue">Open lesson <ArrowRight className="ml-2 h-4 w-4" /></span>
              </Link>
              <Link to="/free-resources" className="bg-royal-blue p-6 text-white shadow-sm transition-transform hover:-translate-y-1">
                <BookOpen className="h-9 w-9 text-kingdom-gold" />
                <h3 className="mt-4 font-heading text-xl font-black">Filter by subject</h3>
                <p className="mt-2 text-sm text-white/80">Turah, Abaryt, reading &amp; language, numbers &amp; math, practical skills, music and more.</p>
              </Link>
              <Link to="/free-resources" className="bg-white p-6 shadow-sm ring-1 ring-black/5 transition-transform hover:-translate-y-1">
                <Users className="h-9 w-9 text-kingdom-gold" />
                <h3 className="mt-4 font-heading text-xl font-black text-royal-blue">Filter by age</h3>
                <p className="mt-2 text-sm text-slate-600">Early learners, youth, teens, adults and household resources in one library.</p>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Pillars Section */}
      <section className="bg-slate-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="heading-section text-royal-blue text-center mb-12">What You'll Find Here</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {pillars.map((pillar, index) => (
              <Card key={pillar.title} className="card-official animate-slide-up" style={{ animationDelay: `${index * 0.1}s` }}>
                <CardHeader>
                  <div className="w-12 h-12 bg-kingdom-gold/10 rounded-full flex items-center justify-center mb-4">
                    <pillar.icon className="w-6 h-6 text-kingdom-gold" />
                  </div>
                  <CardTitle className="heading-card text-royal-blue">{pillar.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-slate-600 text-base">
                    {pillar.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Spotlight Section */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="heading-section text-royal-blue text-center mb-4">Explore</h2>
          <p className="text-center text-slate-600 mb-12 max-w-2xl mx-auto">
            Discover resources, join the movement, and support the nation.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {spotlights.map((item) => {
              const Component = item.external ? "a" : Link;
              const props = item.external
                ? { href: item.href, target: "_blank", rel: "noopener noreferrer" }
                : { to: item.href };

              return (
                <Component
                  key={item.title}
                  {...props}
                  className={`${item.color} text-white p-6 rounded-sm hover:opacity-90 transition-opacity block`}
                  data-testid={`spotlight-${item.title.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <item.icon className="w-8 h-8 mb-4" />
                  <h3 className="font-heading text-xl font-bold mb-2">{item.title}</h3>
                  <p className="text-sm opacity-90">{item.description}</p>
                </Component>
              );
            })}
          </div>
        </div>
      </section>

      {/* Belief Statement */}
      <section className="bg-royal-blue text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="heading-section text-center mb-8">Our Belief</h2>
          <div className="space-y-6 text-slate-300 body-default">
            <p>
              The foundation of this nation, The Kingdom of Yunah'Shach'Qal Yashar'al, is in the Creator of Ha'Shamaym (heavens), Aratz (earth), and Yum (sea). His Name is <YHUHName />, meaning the self-sustaining one. We do not call Him the pagan title of god. His Son's name is <YHUSHAName />, meaning <YHUHName /> delivers. We do not call Him the pagan demon of jesus christ. His Spirit that He sent us after <YHUSHAName /> was seated at His right hand, is the RUCH Ha'Qadash, or the placed apart Spirit.
            </p>
            <p>
              We have been seeking the Ancient Path (YaramYahu/Jeremiah 6:16) for the past 5 years. We are Private Americans, Nationals of United States Republic and self-governed according to the laws outlined by <YHUHName /> in the Qadash Scriptures. We are Ambassadors of Ha'Shamaym. We are in this world but not of it (YahuChanan/John 17:11-15)!
            </p>
            <p>
            We are not apart of any religious nor movement! We believe in what is written in the Qadash Scriptures and the Scriptures alone! We believe <YHUHName /> (YAH) over any man! 
            2 Much in every way! Because firstly indeed, that they were entrusted with the Words of <YHUHName />.
            3 For what if some did not believe? Shall their unbelief nullify the trustworthiness of Aluahym?
            4 'Let it not be! But let <YHUHName /> be true, and every man a liar, as it has been written, “That You should be declared right in Your words, and prevail in Your judging.’” Romaym (Romans) 3:2-4
           </p>
          </div>
          <div className="mt-8 text-center">
            <Link to="/about">
              <Button variant="outline" className="border-white text-white hover:bg-white/10">
                Learn More About Us
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-kingdom-gold py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="heading-section text-white mb-4">Ready to Join?</h2>
          <p className="text-white/90 mb-8 text-lg">
            Become part of a nation built on ecclesiastic law and self-governance.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/become-national">
              <Button className="bg-royal-blue hover:bg-slate-800 text-white px-8 py-3" data-testid="cta-join-btn">
                Become YaSHaRUN
              </Button>
            </Link>
            <Link to="/donate">
              <Button variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-3">
                <Heart className="w-4 h-4 mr-2" />
                Donate / Support
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Qadash Sheep — Rights & Advocacy Lane */}
      <section className="bg-parchment py-16" data-testid="qadash-sheep-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src={QADASH_SHEEP}
                alt="The Qadash Sheep"
                className="rounded-sm shadow-xl max-w-sm mx-auto"
              />
            </div>
            <div>
              <span className="text-kingdom-gold text-sm font-semibold uppercase tracking-wider">
                The Qadash Sheep — Rights &amp; Advocacy
              </span>
              <h2 className="heading-section text-royal-blue mt-2 mb-4">
                Documenting the Fight, Defending the Called-Out
              </h2>
              <p className="text-slate-600 mb-6">
                Rights advocacy, prophetic witness, and the Fight Fi Tash campaign — exposing
                state-sanctioned injustice and standing for the Qadash. For music and artist content,
                visit the <Link to="/brands/shaba" className="text-royal-purple underline">Shaba Sharah</Link> page.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/brands/fightfitash" className="inline-flex items-center gap-2 text-kingdom-gold hover:underline font-semibold" data-testid="home-fightfitash-link">
                  Fight Fi Tash Campaign
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <a
                  href="https://www.youtube.com/@TheWomanInGold77"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-kingdom-gold hover:underline"
                  data-testid="qadash-youtube-link"
                >
                  YouTube
                  <ExternalLink className="w-4 h-4" />
                </a>
                <a
                  href="https://www.tiktok.com/@theqadashsheep77"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-kingdom-gold hover:underline"
                  data-testid="qadash-tiktok-link"
                >
                  TikTok
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
